/**
 * ESCHER FALLS — ステージ名と手がかりの訳。
 *
 * ## なぜレベル JSON に書かないか
 * 41枚の JSON に `nameEn` を足すと、`levelLoader` の検証も
 * `validate-levels` も作り直すことになる。**面の定義は形の話**であって
 * 言語の話ではないので、訳はここに寄せて JSON は触らない。
 *
 * 訳が無い id は元の名前をそのまま出す（新しい面を足した直後でも壊れない）。
 */

import { LANG } from './i18n';

const NAMES: Record<string, string> = {
  '01-first': 'The First Channel',
  '02-turn': 'Stone Bridge',
  '03-fall': 'Falling Water',
  '04-forest': 'Forest of Pillars',
  '05-mill': 'The Mill',
  '06-falls': 'The Falls',
  '07-fork': 'The Fork',
  '08-castle': 'The Impossible Castle',
  '09-rise': 'The Bell Tower',
  '10-plan': 'The Courtyard',
  '11-twice': 'Twin Corridors',
  '12-ascent': 'Without End',
  '13-lever': 'The Stone Arm',
  '14-twohands': 'Two Joints',
  '15-highhand': 'The Lever in the Air',
  '16-ladder': 'The Stone Ladder',
  '17-farjoint': 'The Distant Joint',
  '18-windarm': 'The Arm of Wind',
  '19-twist': 'The Twisted Flume',
  '20-threehands': 'Three Hands',
  '21-balance': 'The Balance',
  'x1-updraft': 'The Upward Fall',
  'x2-wheelrise': 'The Rising Wheel',
  'x3-arch': 'Up and Down Again',
  'x4-siphon': 'The Siphon',
  'x5-twinrise': 'Two Reversals',
  'x6-twogoals': 'Two Basins',
  'x7-share': 'The Dividing Bridge',
  'x8-highshare': 'Divided on High',
  'x9-twinbasin': 'Twin Reversals',
  'x10-summit': 'The Spire of Water',
  'x11-drywell': 'The Dry Well',
  'x12-unchosen': 'The Vessel Unchosen',
  'x13-twobasins': 'Two Catches',
  'x14-silent': 'The Silent Basin',
  'x15-armwell': 'Arm and Empty Well',
  'x16-riseapart': 'Rise and Divide',
  'x17-invabyss': 'The Inverted Abyss',
  'x18-highwell': 'The Empty Well on High',
  'x19-twistabyss': 'The Twisted Abyss',
  'x20-endless': 'The Floor of the Endless',
  'x21-turntable': 'The Turning Dais',
};

const HINTS: Record<string, string> = {
  'x21-turntable': 'Flip the lever and the channel turns with the dais',
  '01-first': 'Rotate the view. Two separate channels line up and become one road.',
  '13-lever': 'Something new can be touched.',
};

/**
 * ステージ名の手がかり（41面ぶん）。**言語ごとに訳してはいない。**
 *
 * 名前は遊びの働きに関わらない飾りなので、日本語以外は英語で揃える。
 * **ここで日本語に落とすと、タイ語やベトナム語の人に日本語が出る**——
 * それは「訳し忘れ」ではなく「読めない文字」になる。
 * 訳が要るのは手がかり（`HINTS`）の方で、あれは遊び方を教える文である。
 */
const HINT_BY_LANG: Record<string, Record<string, string>> = {
  ja: {
    '01-first': '視点を回すと、離れた水路が重なって道になる',
    '13-lever': '触れるものが増えた',
  },
  en: HINTS,
  'zh-Hans': {
    '01-first': '旋转视角，两条分开的水路会重叠成一条路',
    '13-lever': '可以触碰的东西变多了',
  },
  'zh-Hant': {
    '01-first': '旋轉視角，兩條分開的水路會重疊成一條路',
    '13-lever': '可以觸碰的東西變多了',
  },
  ko: {
    '01-first': '시점을 돌리면 떨어져 있던 수로가 겹쳐져 길이 된다',
    '13-lever': '만질 수 있는 것이 늘었다',
  },
  es: {
    '01-first': 'Gira la vista: dos canales separados se superponen y forman un camino',
    '13-lever': 'Ahora hay algo más que puedes tocar',
  },
  pt: {
    '01-first': 'Gire a vista: dois canais separados se sobrepõem e formam um caminho',
    '13-lever': 'Agora há algo mais que você pode tocar',
  },
  ru: {
    '01-first': 'Поверните вид: два раздельных канала совпадут и станут одной дорогой',
    '13-lever': 'Появилось кое-что ещё, к чему можно прикоснуться',
  },
  tr: {
    '01-first': 'Görüşü çevirin: ayrı iki su yolu üst üste gelip tek bir yol olur',
    '13-lever': 'Dokunabileceğiniz bir şey daha var',
  },
  id: {
    '01-first': 'Putar pandangan: dua saluran yang terpisah bertumpuk menjadi satu jalan',
    '13-lever': 'Ada satu hal lagi yang bisa disentuh',
  },
  th: {
    '01-first': 'หมุนมุมมองดู ทางน้ำสองเส้นที่แยกกันจะทับกันกลายเป็นทางเดียว',
    '13-lever': 'มีสิ่งที่แตะได้เพิ่มขึ้น',
  },
  vi: {
    '01-first': 'Quay góc nhìn: hai đường nước tách rời sẽ trùng nhau thành một đường',
    '13-lever': 'Đã có thêm thứ có thể chạm vào',
  },
};

/**
 * 表示するステージ名。
 *
 * 日本語のときだけ元の名前、それ以外は英語名。
 * **読めない文字を出すより、読める英語のほうがましである。**
 */
export function levelName(id: string, fallback: string): string {
  if (LANG === 'ja') return fallback;
  return NAMES[id] ?? fallback;
}

/**
 * 表示する手がかり。**遊び方を教える文なので、ここは訳す。**
 * 訳が無い言語・面では何も出さない（読めない文を出すより無い方がよい）。
 */
export function levelHint(id: string, fallback: string | null): string | null {
  if (fallback === null) return null;
  if (LANG === 'ja') return fallback;
  return HINT_BY_LANG[LANG]?.[id] ?? HINT_BY_LANG.en[id] ?? null;
}
